import * as React from 'react';
import { darken } from 'polished';
import styled from 'styled-components';

import { generateFluidPxScale } from '@/components/blog-2/utils';
import { gridColorSelector, GridRule } from '@/components/v2/GridLines';
import { LayoutSubgrid } from '@/components/v2/Page/LayoutGrid';
import { styles } from '@/components/v2/UtilityCss/Text';
import { parseQuoteWithEmphasis } from '@/utils/parseQuoteWithEmphasis';

import { type CustomerQuoteType } from './CustomerQuote';

interface CustomerQuoteWithLogo extends CustomerQuoteType {
  customer: CustomerQuoteType['customer'] & {
    logo?: {
      url: string;
    };
  };
}

const Section = styled(LayoutSubgrid)`
  position: relative;
  background-color: ${({ theme }) => darken(0.025, theme.palette.fg100)};
  border-top: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
  border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
  margin: 200px 0;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    pointer-events: none;
  }
`;

const CustomerStoriesGrid = styled(LayoutSubgrid)`
  display: grid;
  grid-template-columns: repeat(2, 1fr) !important;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    grid-template-columns: 1fr !important;
  }
`;

const CustomerStory = styled.div`
  grid-column: auto !important;
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    &:not(:last-child) {
      border-bottom: 1px solid
        ${({ theme }) => gridColorSelector(theme, 'major')};
    }
  }
`;

const QuoteWrapper = styled.div`
  padding: 24px ${generateFluidPxScale(12, 32)} 40px
    ${generateFluidPxScale(12, 32)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-items: center;
  place-content: center;
  flex-grow: 1;
`;

const Quote = styled.q`
  text-align: center;
  max-width: 36rem;
  text-wrap: balance;
`;

const Attribution = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 32px;
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;

  p {
    margin: 0;
    text-align: center;
  }

  p:first-child {
    color: ${({ theme }) => theme.palette.fgLoud};
  }
  p:last-child {
    font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  }
`;

const Logo = styled.img`
  margin: 0 auto 16px auto;
  height: 52px;
  max-width: 200px;
  object-fit: contain;
`;

const StyledGridRule = styled(GridRule)`
  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

interface QuotesMultipleProps {
  customerQuotes: CustomerQuoteWithLogo[];
}

const QuotesMultiple: React.ComponentType<QuotesMultipleProps> = React.memo(
  ({ customerQuotes }) => {
    return (
      <Section>
        <LayoutSubgrid data-content-grid data-pile>
          <StyledGridRule $column={6} />
        </LayoutSubgrid>
        <CustomerStoriesGrid data-content-grid data-pile>
          {customerQuotes.map(item => {
            const logoUrl = item.customer.logo?.url;
            return (
              <CustomerStory key={item.customer.name}>
                <QuoteWrapper>
                  {logoUrl && (
                    <Logo alt={`${item.customer.name} logo`} src={logoUrl} />
                  )}
                  <Quote className={styles.l}>
                    "{parseQuoteWithEmphasis(item.quote.quote)}"
                  </Quote>
                  <Attribution>
                    <Author>
                      <Info>
                        <p className={styles.base}>{item.author}</p>
                        <p className={styles.annotation}>
                          {item.customer.name}
                        </p>
                      </Info>
                    </Author>
                  </Attribution>
                </QuoteWrapper>
              </CustomerStory>
            );
          })}
        </CustomerStoriesGrid>
      </Section>
    );
  }
);

export default QuotesMultiple;
